import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Clock, Mail, MapPin, Phone } from "lucide-react";
import Reveal from "@/components/Reveal";
import { PROGRAM_META, SITE } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { usePageMeta } from "@/lib/usePageMeta";

const inputClass =
  "w-full border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 transition-[border-color,box-shadow] duration-200 focus:border-[#0F4C5C] focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20";

export default function Quote() {
  const { t } = useLang();
  usePageMeta(t("meta.quote")[0], t("meta.quote")[1]);
  const insuranceTypes = [...PROGRAM_META.map((p) => t(`programs.${p.id}.title`)), t("quotePage.otherType")];
  const [form, setForm] = useState({ name: "", email: "", phone: "", insurance_type: "", message: "", website: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setError("");
    try {
      await api.post("/quotes", form);
      setStatus("success");
    } catch (err) {
      setError(err?.response?.status === 429 ? t("quotePage.rateLimitError") : formatApiError(err));
      setStatus("error");
    }
  };

  return (
    <>
      <section className="bg-[#0F4C5C] py-14 text-white sm:py-20" data-testid="quote-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="inline-flex items-center gap-2 text-base font-extrabold uppercase tracking-[0.22em] text-[#E8A34C]">
            <span className="block h-px w-8 bg-[#E8A34C]" aria-hidden="true" />
            {t("quotePage.heroOverline")}
          </p>
          <h1 className="font-heading mt-4 max-w-3xl text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
            {t("quotePage.heroTitle")}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-200 sm:text-lg">
            {t("quotePage.heroSub")}
          </p>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-14 sm:py-20" data-testid="quote-form-section">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[1.5fr_1fr] lg:px-8">
          <Reveal>
            <div className="border border-[#0F4C5C]/10 bg-white p-6 shadow-lg shadow-slate-900/5 sm:p-10">
              {status === "success" ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="py-10 text-center"
                  data-testid="quote-success-message"
                >
                  <span className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#EEF6FC]">
                    <CheckCircle2 className="h-8 w-8 text-[#0F4C5C]" aria-hidden="true" />
                  </span>
                  <h2 className="font-heading text-2xl font-bold text-slate-900 sm:text-3xl">{t("quotePage.successTitle")}</h2>
                  <p className="mx-auto mt-3 max-w-md text-slate-600">
                    {t("quotePage.successSub")}
                  </p>
                  <div className="mt-7 flex flex-wrap justify-center gap-3">
                    <a href={SITE.phone1Href} className="inline-flex items-center gap-2 bg-[#0F4C5C] px-6 py-3 text-sm font-bold text-white" data-testid="quote-success-call-btn">
                      <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phone1}
                    </a>
                    <Link to="/" className="inline-flex items-center gap-2 border border-[#0F4C5C] px-6 py-3 text-sm font-bold text-[#0F4C5C]" data-testid="quote-success-home-btn">
                      {t("common.backToHome")}
                    </Link>
                  </div>
                </motion.div>
              ) : (
                <form onSubmit={submit} noValidate={false} data-testid="quote-form">
                  <h2 className="font-heading text-xl font-bold text-slate-900 sm:text-2xl">{t("quotePage.formTitle")}</h2>
                  <p className="mt-2 text-sm text-slate-600">{t("quotePage.formSub")}</p>
                  <div className="absolute -left-[9999px] -top-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
                    <label htmlFor="quote-website">Website</label>
                    <input id="quote-website" type="text" name="website" value={form.website} onChange={update("website")} tabIndex={-1} autoComplete="off" data-testid="quote-honeypot-input" />
                  </div>
                  <div className="mt-8 grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="quote-name" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-700">
                        {t("quotePage.labelName")}
                      </label>
                      <input id="quote-name" type="text" required value={form.name} onChange={update("name")} className={inputClass} placeholder={t("quotePage.phName")} autoComplete="name" data-testid="quote-name-input" />
                    </div>
                    <div>
                      <label htmlFor="quote-phone" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-700">
                        {t("quotePage.labelPhone")}
                      </label>
                      <input id="quote-phone" type="tel" required value={form.phone} onChange={update("phone")} className={inputClass} placeholder={t("quotePage.phPhone")} autoComplete="tel" data-testid="quote-phone-input" />
                    </div>
                    <div>
                      <label htmlFor="quote-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-700">
                        {t("quotePage.labelEmail")}
                      </label>
                      <input id="quote-email" type="email" required value={form.email} onChange={update("email")} className={inputClass} placeholder={t("quotePage.phEmail")} autoComplete="email" data-testid="quote-email-input" />
                    </div>
                    <div>
                      <label htmlFor="quote-type" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-700">
                        {t("quotePage.labelType")}
                      </label>
                      <select id="quote-type" required value={form.insurance_type} onChange={update("insurance_type")} className={inputClass} data-testid="quote-type-select">
                        <option value="" disabled>
                          {t("quotePage.phType")}
                        </option>
                        {insuranceTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="quote-message" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-700">
                        {t("quotePage.labelMessage")}
                      </label>
                      <textarea id="quote-message" rows={5} value={form.message} onChange={update("message")} className={inputClass} placeholder={t("quotePage.phMessage")} data-testid="quote-message-input" />
                    </div>
                  </div>
                  {status === "error" && (
                    <p role="alert" className="mt-4 border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="quote-error-message">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="mt-7 w-full bg-[#D97706] px-6 py-4 text-sm font-bold uppercase tracking-wider text-white transition-[background-color,transform,opacity] duration-200 hover:-translate-y-0.5 hover:bg-[#B45309] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                    data-testid="quote-form-submit-btn"
                  >
                    {status === "submitting" ? t("quotePage.sending") : t("quotePage.submitBtn")}
                  </button>
                </form>
              )}
            </div>
          </Reveal>

          <div className="space-y-6">
            <Reveal delay={1}>
              <div className="bg-[#0F4C5C] p-7 text-white" data-testid="quote-contact-card">
                <h2 className="font-heading text-lg font-bold">{t("quotePage.sideTitle")}</h2>
                <p className="mt-2 text-sm text-slate-300">{t("quotePage.sideSub")}</p>
                <div className="mt-5 space-y-3 text-sm">
                  <a href={SITE.phone1Href} className="flex items-center gap-3 font-semibold hover:text-[#E8A34C] transition-colors duration-200" data-testid="quote-sidebar-phone-1">
                    <Phone className="h-4 w-4 text-[#E8A34C]" aria-hidden="true" /> {SITE.phone1} {t("quotePage.armandoSuffix")}
                  </a>
                  <a href={SITE.phone2Href} className="flex items-center gap-3 font-semibold hover:text-[#E8A34C] transition-colors duration-200" data-testid="quote-sidebar-phone-2">
                    <Phone className="h-4 w-4 text-[#E8A34C]" aria-hidden="true" /> {SITE.phone2} {t("quotePage.dianaSuffix")}
                  </a>
                  <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 hover:text-[#E8A34C] transition-colors duration-200" data-testid="quote-sidebar-email">
                    <Mail className="h-4 w-4 text-[#E8A34C]" aria-hidden="true" /> {SITE.email}
                  </a>
                </div>
              </div>
            </Reveal>
            <Reveal delay={2}>
              <div className="border border-[#0F4C5C]/10 bg-white" data-testid="quote-location-card">
                <iframe
                  title="Map to A&F Insurance Group, Inc. - The Exchange Building, 3333 West Commercial Blvd., Suite 203, Fort Lauderdale, FL 33309"
                  src="https://maps.google.com/maps?q=3333%20W%20Commercial%20Blvd%20Suite%20203%2C%20Fort%20Lauderdale%2C%20FL%2033309&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  className="h-64 w-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="quote-map"
                />
                <div className="p-7">
                  <h2 className="font-heading text-lg font-bold text-slate-900">{t("quotePage.officeTitle")}</h2>
                  <p className="mt-3 flex items-start gap-3 text-sm leading-relaxed text-slate-600">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#D97706]" aria-hidden="true" />
                    <span>
                      {SITE.address1}
                      <br />
                      {SITE.address2}
                      <br />
                      {SITE.address3}
                    </span>
                  </p>
                  <p className="mt-3 flex items-center gap-3 text-sm text-slate-600">
                    <Clock className="h-4 w-4 text-[#D97706]" aria-hidden="true" /> {t("common.hours")}
                  </p>
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=3333+W+Commercial+Blvd+Suite+203,+Fort+Lauderdale,+FL+33309"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#0F4C5C] transition-colors duration-200 hover:text-[#D97706]"
                    data-testid="quote-directions-link"
                  >
                    {t("quotePage.getDirections")}
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
